import { Button } from "@adasupport/byron";
// Moment is no longer supported. Slack channel #p-remove-moment for details
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import useOnClickOutside from "react-cool-onclickoutside";
import { useSelector } from "react-redux";

import { Button as CommonButton } from "components/Common/Button";
import { DoubleDatePicker } from "components/Common/DoubleDatePicker";
import { InfoTooltip } from "components/Common/InfoTooltip";
import { InputLabel } from "components/Common/InputLabel";
import { InputText } from "components/Common/InputText";
import SvgIcon from "components/Common/SvgIcon";
import { useComponentDidMount } from "hooks/useComponentDidMount";
import { getResource } from "selectors/resources";
import {
  FIRST_DAY_OF_LAST_MONTH_LOCAL,
  FIRST_DAY_OF_LAST_YEAR_LOCAL,
  LAST_DAY_OF_LAST_MONTH_LOCAL,
  LAST_DAY_OF_LAST_YEAR_LOCAL,
  NINETY_DAYS_AGO_LOCAL,
  ONE_HUNDRED_EIGHTY_DAYS_AGO_LOCAL,
  SEVEN_DAYS_AGO_LOCAL,
  START_OF_THIS_YEAR_LOCAL,
  THIRTY_DAYS_AGO_LOCAL,
  TODAY_LOCAL,
  YESTERDAY_LOCAL,
  getDateRangeString,
} from "services/date-strings";
import { keys } from "services/helpers";
import {
  type TIME_ZONES_CANONICAL,
  getTimezoneLabel,
  getTimezoneUTCOffset,
} from "services/timezones";

import {
  DATE_FORMAT,
  DROPDOWN_OPTIONS,
  type DatesInputState,
  type DatesState,
  END_DATE_INPUT,
  type ExcludeOptions,
  START_DATE_INPUT,
  TIME_INTERVALS,
} from "./constants";
import {
  formattedDateInput,
  getMinStartDate,
  isDateRangeValid,
  isValidDate,
} from "./services";
import * as S from "./styles";

export const DoubleDatepickerContent = ({
  startDate,
  endDate,
  onChange,
  minDate,
  maxDate,
  excludeOptions,
  titleTooltip,
  disclaimer,
  disclaimerToolTip,
  allTimeOption,
  setText,
  minDateTooltip,
}: {
  startDate: string;
  endDate: string;
  onChange: (startDate: string, endDate: string) => void;
  minDate?: string | undefined;
  maxDate?: string | undefined;
  excludeOptions?: ExcludeOptions[];
  titleTooltip?: string | undefined;
  disclaimer?: string | undefined;
  disclaimerToolTip?: string | undefined;
  allTimeOption?: boolean;
  setText?: (text: string) => void | undefined;
  minDateTooltip?: string;
}) => {
  const [selectingEndDate, setSelectingEndDate] = useState<boolean>(false);
  const [inputDates, setInputDates] = useState<DatesInputState>({
    startDateState: moment.utc(startDate).format("DD/MM/YYYY"),
    startDateValid: true,
    endDateState: moment.utc(endDate).format("DD/MM/YYYY"),
    endDateValid: true,
    highlightInvalid: false,
  });
  const [focusedInput, setFocusedInput] = useState<string>(START_DATE_INPUT);

  const user = useSelector((state) => getResource(state, "user"));
  const timeZone = (user?.timeZone ||
    "UTC") as keyof typeof TIME_ZONES_CANONICAL;

  const startInputEle = useRef<HTMLTextAreaElement>(null);
  const endInputEle = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const updatedInputDates = {
      startDateState: "",
      startDateValid: false,
      endDateState: "",
      endDateValid: false,
      highlightInvalid: false,
    };

    if (startDate) {
      updatedInputDates.startDateState = moment
        .utc(startDate)
        .format("DD/MM/YYYY");
      updatedInputDates.startDateValid = true;
    }

    if (endDate) {
      updatedInputDates.endDateState = moment.utc(endDate).format("DD/MM/YYYY");
      updatedInputDates.endDateValid = true;
    }

    setInputDates(updatedInputDates);
  }, [startDate, endDate]);

  const handleDateInput =
    (inputType: string) =>
    (value: string): void => {
      const newValue = formattedDateInput(value);
      const newValueISOFormat = moment
        .utc(newValue, "DD/MM/YYYY")
        .format("YYYY-MM-DD");

      const startDateStr =
        inputType === START_DATE_INPUT ? newValueISOFormat : startDate;
      const endDateStr =
        inputType === END_DATE_INPUT ? newValueISOFormat : endDate;

      const valid =
        isValidDate(newValue) &&
        moment.utc(newValueISOFormat).isValid() &&
        isDateRangeValid(startDateStr, endDateStr, minDate, maxDate);

      if (inputType === START_DATE_INPUT) {
        setInputDates({
          ...inputDates,
          startDateState: newValue,
          startDateValid: valid,
        });

        if (valid) {
          onChange(newValueISOFormat, endDate);

          setSelectingEndDate(true);
          endInputEle.current?.focus();
        }
      } else if (inputType === END_DATE_INPUT) {
        setInputDates({
          ...inputDates,
          endDateState: newValue,
          endDateValid: valid,
        });

        if (valid) {
          onChange(startDate, newValueISOFormat);
        }
      }
    };

  const handleCalendarDateSelect = (selectedDate: string) => {
    const momentDate = moment.utc(selectedDate);

    if (momentDate.isBefore(startDate) || !selectingEndDate) {
      onChange(momentDate.format(DATE_FORMAT), momentDate.format(DATE_FORMAT));
      setSelectingEndDate(true);
    } else {
      onChange(startDate, momentDate.format(DATE_FORMAT));
      setSelectingEndDate(false);
    }
  };

  const handleDropdownValueSelect = (value: string) => () => {
    switch (value) {
      case TIME_INTERVALS.today: {
        return onChange(getMinStartDate(TODAY_LOCAL, minDate), TODAY_LOCAL);
      }

      case TIME_INTERVALS.yesterday: {
        return onChange(
          getMinStartDate(YESTERDAY_LOCAL, minDate),
          YESTERDAY_LOCAL,
        );
      }

      case TIME_INTERVALS.yearToDate: {
        return onChange(
          getMinStartDate(START_OF_THIS_YEAR_LOCAL, minDate),
          TODAY_LOCAL,
        );
      }

      case TIME_INTERVALS.last7Days: {
        return onChange(
          getMinStartDate(SEVEN_DAYS_AGO_LOCAL, minDate),
          YESTERDAY_LOCAL,
        );
      }

      case TIME_INTERVALS.last30Days: {
        return onChange(
          getMinStartDate(THIRTY_DAYS_AGO_LOCAL, minDate),
          YESTERDAY_LOCAL,
        );
      }

      case TIME_INTERVALS.last90Days: {
        return onChange(
          getMinStartDate(NINETY_DAYS_AGO_LOCAL, minDate),
          YESTERDAY_LOCAL,
        );
      }

      case TIME_INTERVALS.last180Days: {
        return onChange(
          getMinStartDate(ONE_HUNDRED_EIGHTY_DAYS_AGO_LOCAL, minDate),
          YESTERDAY_LOCAL,
        );
      }

      case TIME_INTERVALS.lastMonth: {
        return onChange(
          getMinStartDate(FIRST_DAY_OF_LAST_MONTH_LOCAL, minDate),
          LAST_DAY_OF_LAST_MONTH_LOCAL,
        );
      }

      case TIME_INTERVALS.lastYear: {
        return onChange(
          getMinStartDate(FIRST_DAY_OF_LAST_YEAR_LOCAL, minDate),
          LAST_DAY_OF_LAST_YEAR_LOCAL,
        );
      }

      case TIME_INTERVALS.custom: {
        setSelectingEndDate(false);
        startInputEle.current?.focus();

        return onChange("", "");
      }

      case TIME_INTERVALS.alltime: {
        return (
          minDate &&
          maxDate &&
          onChange(
            moment.utc(minDate).format("YYYY-MM-DD"),
            moment.utc(maxDate).format("YYYY-MM-DD"),
          )
        );
      }

      default: {
        return null;
      }
    }
  };

  const getDropdownString = (
    timeIntervalString: keyof typeof DROPDOWN_OPTIONS,
  ) => {
    if (timeIntervalString !== TIME_INTERVALS.custom) {
      return DROPDOWN_OPTIONS[timeIntervalString];
    }

    const startDateString = moment(startDate);
    const endDateString = moment(endDate);

    if (startDateString.isValid() && endDateString.isValid()) {
      return `${startDateString.format("DD/MM/YYYY")} – ${endDateString.format(
        "DD/MM/YYYY",
      )}`;
    }

    return "Choose Dates...";
  };

  const matchedDateRangeString = (): keyof typeof DROPDOWN_OPTIONS => {
    let dateRanges: string[] = [];

    if (allTimeOption && minDate === startDate && maxDate === endDate) {
      dateRanges = ["alltime"];
    } else {
      dateRanges = getDateRangeString(startDate, endDate, {
        allMatches: true,
      }) as string[];
    }

    return (dateRanges.find(
      (str) => (DROPDOWN_OPTIONS as Record<string, unknown>)[str],
    ) || TIME_INTERVALS.custom) as keyof typeof DROPDOWN_OPTIONS;
  };

  useComponentDidMount(() => {
    setText?.(getDropdownString(matchedDateRangeString()));
  });

  const startInputHelperMessage =
    !inputDates.startDateValid && focusedInput === START_DATE_INPUT
      ? "Date must be selected"
      : "";
  const endInputHelperMessage =
    !inputDates.endDateValid && focusedInput === END_DATE_INPUT
      ? "Date must be selected"
      : "";

  return (
    <S.DoubleDatepickerContent>
      {titleTooltip && (
        <S.InfoTooltipWrapper>
          <InfoTooltip
            blurb={titleTooltip}
            iconDefault="Info"
            iconClicked="InfoFilled"
            openOnHover
          />
        </S.InfoTooltipWrapper>
      )}
      <S.ListItemGroup>
        {keys(DROPDOWN_OPTIONS).map((key) => {
          if (key === "alltime" && !allTimeOption) {
            return null;
          }

          const label = DROPDOWN_OPTIONS[key];
          const selected = DROPDOWN_OPTIONS[matchedDateRangeString()] === label;
          const disabled =
            excludeOptions && excludeOptions.includes(key as ExcludeOptions);

          return (
            <li key={key}>
              <S.ListItemButton
                onClick={!disabled ? handleDropdownValueSelect(key) : undefined}
                selected={selected}
                disabled={disabled}
              >
                <S.ListItemLabel>{label}</S.ListItemLabel>
                {selected && (
                  <S.ListItemIcon>
                    <SvgIcon icon="Checkmark" height={20} />
                  </S.ListItemIcon>
                )}
              </S.ListItemButton>
            </li>
          );
        })}
      </S.ListItemGroup>
      <S.CalendarGroup>
        <S.CalendarInputs>
          <S.DateInput>
            <InputLabel label="Starting">
              <InputText
                value={inputDates.startDateState}
                helperMessage={startInputHelperMessage}
                invalid={
                  !inputDates.startDateValid && inputDates.highlightInvalid
                }
                invalidMessage="Date must be selected"
                placeholder="DD/MM/YYYY"
                onChange={handleDateInput(START_DATE_INPUT)}
                onFocus={() => setFocusedInput(START_DATE_INPUT)}
                higherRef={startInputEle}
              />
            </InputLabel>
          </S.DateInput>
          <S.DateInput>
            <InputLabel label="Ending">
              <InputText
                value={inputDates.endDateState}
                helperMessage={endInputHelperMessage}
                invalid={
                  !inputDates.endDateValid && inputDates.highlightInvalid
                }
                invalidMessage="Date must be selected"
                placeholder="DD/MM/YYYY"
                onChange={handleDateInput(END_DATE_INPUT)}
                onFocus={() => setFocusedInput(END_DATE_INPUT)}
                higherRef={endInputEle}
              />
            </InputLabel>
          </S.DateInput>
        </S.CalendarInputs>

        <S.Calendar>
          <DoubleDatePicker
            startDate={startDate}
            endDate={endDate}
            maxDate={maxDate}
            minDate={minDate}
            onChange={handleCalendarDateSelect}
            minDateTooltip={minDateTooltip}
          />
        </S.Calendar>

        <S.Timezone>
          Dates are in {getTimezoneLabel(timeZone)} [
          {getTimezoneUTCOffset(timeZone)}]
        </S.Timezone>

        {disclaimer && (
          <S.Disclaimer>
            {disclaimer}
            {disclaimerToolTip && (
              <InfoTooltip
                blurb={disclaimerToolTip}
                iconDefault="QuestionCircle"
                openOnHover
                above
              />
            )}
          </S.Disclaimer>
        )}
      </S.CalendarGroup>
    </S.DoubleDatepickerContent>
  );
};

export const DoubleDatepickerDropdown = ({
  startDate,
  endDate,
  onChange,
  minDate,
  maxDate,
  excludeOptions,
  titleTooltip,
  fullWidth,
  disclaimer,
  disclaimerToolTip,
  allTimeOption,
  setText,
  minDateTooltip,
  anchor = "left",
}: {
  startDate: string;
  endDate: string;
  onChange: (startDate: string, endDate: string) => void;
  minDate?: string | undefined;
  maxDate?: string | undefined;
  excludeOptions?: ExcludeOptions[];
  titleTooltip?: string | undefined;
  fullWidth?: boolean;
  disclaimer?: string | undefined;
  disclaimerToolTip?: string | undefined;
  allTimeOption?: boolean;
  setText?: (text: string) => void | undefined;
  minDateTooltip?: string;
  anchor?: "left" | "right";
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedDates, setSelectedDates] = useState<DatesState>({
    startDateState: startDate,
    endDateState: endDate,
  });
  const [inputDates, setInputDates] = useState<DatesInputState>({
    startDateState: moment.utc(startDate).format("DD/MM/YYYY"),
    startDateValid: true,
    endDateState: moment.utc(endDate).format("DD/MM/YYYY"),
    endDateValid: true,
    highlightInvalid: false,
  });

  const handleCancel = (): void => {
    setSelectedDates({ startDateState: startDate, endDateState: endDate });
    setIsOpen(false);
  };

  const ref = useOnClickOutside(() => {
    // Reset to original values (same as Cancel button)
    handleCancel();
  });

  useEffect(() => {
    setSelectedDates({ startDateState: startDate, endDateState: endDate });
  }, [startDate, endDate]);

  useEffect(() => {
    const { startDateState, endDateState } = selectedDates;
    const updatedInputDates = {
      startDateState: "",
      startDateValid: false,
      endDateState: "",
      endDateValid: false,
      highlightInvalid: false,
    };

    if (startDateState) {
      updatedInputDates.startDateState = moment
        .utc(startDateState)
        .format("DD/MM/YYYY");
      updatedInputDates.startDateValid = true;
    }

    if (endDateState) {
      updatedInputDates.endDateState = moment
        .utc(endDateState)
        .format("DD/MM/YYYY");
      updatedInputDates.endDateValid = true;
    }

    setInputDates(updatedInputDates);
  }, [selectedDates]);

  const toggleDropdownMenu = () => {
    setIsOpen(!isOpen);
  };

  const handleApply = (): void => {
    if (inputDates.startDateValid && inputDates.endDateValid) {
      const { startDateState, endDateState } = selectedDates;
      onChange(startDateState, endDateState);
      setIsOpen(false);
    } else {
      setInputDates({ ...inputDates, highlightInvalid: true });
    }
  };

  const getDropdownString = (
    timeIntervalString: keyof typeof DROPDOWN_OPTIONS,
  ) => {
    if (timeIntervalString !== TIME_INTERVALS.custom) {
      return DROPDOWN_OPTIONS[timeIntervalString];
    }

    const startDateString = moment(selectedDates.startDateState);
    const endDateString = moment(selectedDates.endDateState);

    if (startDateString.isValid() && endDateString.isValid()) {
      return `${startDateString.format("DD/MM/YYYY")} – ${endDateString.format(
        "DD/MM/YYYY",
      )}`;
    }

    return "Choose Dates...";
  };

  const matchedDateRangeString = (): keyof typeof DROPDOWN_OPTIONS => {
    let dateRanges: string[] = [];

    if (
      allTimeOption &&
      minDate === selectedDates.startDateState &&
      maxDate === selectedDates.endDateState
    ) {
      dateRanges = ["alltime"];
    } else {
      dateRanges = getDateRangeString(
        selectedDates.startDateState,
        selectedDates.endDateState,
        { allMatches: true },
      ) as string[];
    }

    return (dateRanges.find(
      (str) => (DROPDOWN_OPTIONS as Record<string, unknown>)[str],
    ) || TIME_INTERVALS.custom) as keyof typeof DROPDOWN_OPTIONS;
  };

  useComponentDidMount(() => {
    setText?.(getDropdownString(matchedDateRangeString()));
  });

  return (
    <div ref={ref}>
      <CommonButton
        onClick={toggleDropdownMenu}
        icon="Calendar"
        iconHeightOverride={24}
        secondIcon="ChevronDown"
        secondIconHeightOverride={24}
        text={getDropdownString(matchedDateRangeString())}
        light
        stretched={fullWidth}
        left
        secondIconGroupRight
      />

      {isOpen && (
        <S.DropDownMenu anchor={anchor}>
          <DoubleDatepickerContent
            startDate={selectedDates.startDateState}
            endDate={selectedDates.endDateState}
            onChange={(newStartDate, newEndDate) => {
              setSelectedDates({
                startDateState: newStartDate,
                endDateState: newEndDate,
              });
            }}
            minDate={minDate}
            maxDate={maxDate}
            excludeOptions={excludeOptions}
            titleTooltip={titleTooltip}
            disclaimer={disclaimer}
            disclaimerToolTip={disclaimerToolTip}
            allTimeOption={allTimeOption}
            setText={setText}
            minDateTooltip={minDateTooltip}
          />

          <S.DropDownActions>
            <Button onClick={handleCancel} text="Cancel" variant="secondary" />
            <Button variant="primary" onClick={handleApply} text="Apply" />
          </S.DropDownActions>
        </S.DropDownMenu>
      )}
    </div>
  );
};
